import {
  RuleCombinator,
  RuleConditionRequest,
  RuleConditionResponse,
  RuleOperator,
  RuleRequest,
  RuleVersion,
} from 'utils/api/modules/memberGroups';
import { UuidableName } from 'utils/api/types';
import { UserFieldDropdownKind } from 'utils/api/modules/user/user.types';

// The FE analogue of the backend's rules.py registry. Attribute keys and operator wire-names must
// agree with the backend.

export const RULE_MAX_CONDITIONS = 20;
export const INLINE_VALUE_LIMIT = 5;
export const CUSTOM_FIELD_PREFIX = 'custom_field:';
export const CUSTOM_FIELD_SLOTS = [1, 2, 3, 4, 5] as const;

export type AttributeKind = 'option' | 'site' | 'manager' | 'country' | 'text';

export const Attribute = {
  Department: 'department',
  JobTitle: 'job_title',
  Site: 'site',
  Manager: 'manager',
  CostCentre: 'cost_centre',
  EmploymentType: 'employment_type',
  Region: 'region',
  Country: 'country',
} as const;

export const ATTRIBUTE_ORDER: string[] = [
  Attribute.Department,
  Attribute.JobTitle,
  Attribute.Site,
  Attribute.Manager,
  Attribute.CostCentre,
  Attribute.EmploymentType,
  Attribute.Region,
  Attribute.Country,
  ...CUSTOM_FIELD_SLOTS.map((slot) => `${CUSTOM_FIELD_PREFIX}${slot}`),
];

// Region + custom fields are hidden for orgs without Object Manager. Site is hidden for
// orgs without the Sites entity module.
const OBJECT_MANAGER_ONLY_ATTRIBUTES: ReadonlySet<string> = new Set<string>([
  Attribute.Region,
  ...CUSTOM_FIELD_SLOTS.map((slot) => `${CUSTOM_FIELD_PREFIX}${slot}`),
]);

export interface AttributeVisibility {
  hasObjectManager: boolean;
  hasSites: boolean;
}

export const visibleAttributes = ({ hasObjectManager, hasSites }: AttributeVisibility): string[] =>
  ATTRIBUTE_ORDER.filter((attr) => {
    const hiddenAsObjectManagerOnly = !hasObjectManager && OBJECT_MANAGER_ONLY_ATTRIBUTES.has(attr);
    const hiddenAsSitesOnly = !hasSites && attr === Attribute.Site;
    return !hiddenAsObjectManagerOnly && !hiddenAsSitesOnly;
  });

export const ATTRIBUTE_LABELS: Record<string, string> = {
  [Attribute.Department]: 'Department',
  [Attribute.JobTitle]: 'Job title',
  [Attribute.Site]: 'Site',
  [Attribute.Manager]: 'Line manager',
  [Attribute.CostCentre]: 'Cost centre',
  [Attribute.EmploymentType]: 'Employment type',
  [Attribute.Region]: 'Region',
  [Attribute.Country]: 'Country',
};

const ATTRIBUTE_KINDS: Record<string, AttributeKind> = {
  [Attribute.Department]: 'option',
  [Attribute.Region]: 'option',
  [Attribute.EmploymentType]: 'option',
  [Attribute.Site]: 'site',
  [Attribute.Manager]: 'manager',
  [Attribute.Country]: 'country',
  [Attribute.JobTitle]: 'text',
  [Attribute.CostCentre]: 'text',
};

const OPTION_KIND_SLUGS: Record<string, UserFieldDropdownKind> = {
  [Attribute.Department]: 'department',
  [Attribute.Region]: 'region',
  [Attribute.EmploymentType]: 'employment-type',
};

export const OPERATOR_LABELS: Record<RuleOperator, string> = {
  is: 'is',
  isNot: 'is not',
  isAnyOf: 'is any of',
  isNoneOf: 'is none of',
  contains: 'contains',
  doesNotContain: 'does not contain',
  startsWith: 'starts with',
  isEmpty: 'is empty',
  isNotEmpty: 'is not empty',
};

const DROPDOWN_OPERATORS: RuleOperator[] = ['is', 'isNot', 'isAnyOf', 'isNoneOf'];
const TEXT_OPERATORS: RuleOperator[] = [
  'is',
  'isNot',
  'contains',
  'doesNotContain',
  'startsWith',
  'isEmpty',
  'isNotEmpty',
];

const MULTI_VALUE_OPERATORS: ReadonlySet<RuleOperator> = new Set<RuleOperator>(['isAnyOf', 'isNoneOf']);
const NO_VALUE_OPERATORS: ReadonlySet<RuleOperator> = new Set<RuleOperator>(['isEmpty', 'isNotEmpty']);

export const isCustomField = (attribute: string): boolean => attribute.startsWith(CUSTOM_FIELD_PREFIX);

export const customFieldSlot = (attribute: string): number | null => {
  if (!isCustomField(attribute)) {
    return null;
  }
  const raw = attribute.slice(CUSTOM_FIELD_PREFIX.length);
  const slot = Number(raw);
  return CUSTOM_FIELD_SLOTS.includes(slot as (typeof CUSTOM_FIELD_SLOTS)[number]) ? slot : null;
};

export const attributeKind = (attribute: string): AttributeKind =>
  isCustomField(attribute) ? 'text' : (ATTRIBUTE_KINDS[attribute] ?? 'text');

export const isTextKind = (attribute: string): boolean => attributeKind(attribute) === 'text';

export const optionKindSlug = (attribute: string): UserFieldDropdownKind | null => OPTION_KIND_SLUGS[attribute] ?? null;

export const operatorsForAttribute = (attribute: string): RuleOperator[] =>
  isTextKind(attribute) ? TEXT_OPERATORS : DROPDOWN_OPERATORS;

export const isMultiValueOperator = (operator: RuleOperator): boolean => MULTI_VALUE_OPERATORS.has(operator);
export const isNoValueOperator = (operator: RuleOperator): boolean => NO_VALUE_OPERATORS.has(operator);

export type RuleValue = string | UuidableName;

export interface RuleConditionDraft {
  attribute: string;
  operator: RuleOperator;
  values: RuleValue[];
}

export interface RuleDraft {
  combinator: RuleCombinator;
  conditions: RuleConditionDraft[];
}

export const conditionHasValue = (condition: RuleConditionDraft): boolean => {
  if (isNoValueOperator(condition.operator)) {
    return true;
  }
  return condition.values.some((value) => (typeof value === 'string' ? value.trim() !== '' : Boolean(value?.uuid)));
};

export const firstUsableAttribute = (used: Set<string> = new Set(), pool: string[] = ATTRIBUTE_ORDER): string =>
  pool.find((attr) => !used.has(attr) && attributeKind(attr) === 'option') ??
  pool.find((attr) => !used.has(attr) && isTextKind(attr)) ??
  pool.find((attr) => !used.has(attr)) ??
  pool[0];

export const newCondition = (used: Set<string> = new Set(), pool: string[] = ATTRIBUTE_ORDER): RuleConditionDraft => ({
  attribute: firstUsableAttribute(used, pool),
  operator: 'is',
  values: [],
});

const valueToWire = (value: RuleValue): string => (typeof value === 'string' ? value : value.uuid);

export const serializeCondition = (condition: RuleConditionDraft): RuleConditionRequest => {
  if (isNoValueOperator(condition.operator)) {
    return { attribute: condition.attribute, operator: condition.operator };
  }
  return {
    attribute: condition.attribute,
    operator: condition.operator,
    values: condition.values.map(valueToWire).filter((value) => value.trim() !== ''),
  };
};

export const serializeRule = (rule: RuleDraft): RuleRequest => ({
  combinator: rule.combinator,
  conditions: rule.conditions.map(serializeCondition),
});

export const draftFromRule = (rule: RuleVersion): RuleDraft => ({
  combinator: rule.combinator,
  conditions: rule.conditions.map((condition: RuleConditionResponse) => ({
    attribute: condition.attribute,
    operator: condition.operator,
    values: condition.values as RuleValue[],
  })),
});
